import React from 'react';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { Locale } from 'date-fns/locale';
import { isDayDisabled, isInSelectedRange } from './utils';

interface DayCellProps {
    day: Date;
    monthStart: Date;
    minDate: Date;
    maxDate: Date;
    disabledDays: Date[];
    localSelectedDate: Date;
    rangeStart: Date | null;
    rangeEnd: Date | null;
    hoverDate: Date | null;
    event: Event | undefined;
    onDateClick: (day: Date) => void;
    onMouseEnter: (day: Date) => void;
    onMouseLeave: () => void;
    locale: Locale;
}

const DayCell: React.FC<DayCellProps> = ({
    day,
    monthStart,
    minDate,
    maxDate,
    disabledDays,
    localSelectedDate,
    rangeStart,
    rangeEnd,
    hoverDate,
    event,
    onDateClick,
    onMouseEnter,
    onMouseLeave,
    locale
}) => {
    // Determine various day states
    const isToday = isSameDay(day, new Date());
    const selected = isSameDay(day, localSelectedDate);
    const isSameMonthDay = isSameMonth(day, monthStart);
    const disabled = isDayDisabled(day, disabledDays, minDate, maxDate);
    const inRange = isInSelectedRange(day, rangeStart, rangeEnd, hoverDate);
    const isRangeStart = rangeStart && isSameDay(day, rangeStart);
    const isRangeEnd = rangeEnd && isSameDay(day, rangeEnd);
    const isHoverDate = hoverDate && isSameDay(day, hoverDate);

    // Base cell class with consistent sizing and positioning
    let cellClass = "relative flex items-center justify-center h-9 w-9 md:h-10 md:w-10 mx-auto rounded-full transition-colors duration-150 ";

    // Day not in current month - improved for dark mode
    if (!isSameMonthDay) {
        cellClass += "text-gray-300 dark:text-gray-600 ";
    }
    // Disabled days - improved contrast for dark mode
    else if (disabled) {
        cellClass += "text-gray-400 dark:text-gray-500 cursor-not-allowed ";
    }
    // Regular day - improved base text colors for dark mode
    else {
        cellClass += "text-gray-800 dark:text-gray-200 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ";
    }

    // Today styling - improved border visibility in dark mode
    if (isToday && !selected && !isRangeStart && !isRangeEnd) {
        cellClass += "border-2 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-300 ";
    }

    // Selected day styling - consistent across modes
    if (selected || isRangeStart || isRangeEnd) {
        cellClass += "bg-blue-600 dark:bg-blue-500 text-white font-medium shadow-sm ";
    }

    // Range styling - better contrast in dark mode
    if (inRange && !isRangeStart && !isRangeEnd) {
        cellClass += "bg-blue-100 dark:bg-blue-800 dark:bg-opacity-40 text-blue-800 dark:text-blue-200 ";
    }

    // Hover date styling - consistent across modes
    if (isHoverDate && !isRangeStart && !isRangeEnd) {
        cellClass += "bg-blue-400 dark:bg-blue-600 text-white ";
    }

    return (
        <div
            className="py-1 relative"
            onMouseEnter={() => onMouseEnter(day)}
            onMouseLeave={onMouseLeave}
        >
            <button
                className={cellClass}
                onClick={() => !disabled && onDateClick(day)}
                disabled={disabled}
                aria-selected={selected || isRangeStart || isRangeEnd}
                aria-disabled={disabled}
                role="gridcell"
                title={format(day, 'PPPP', { locale })}
            >
                {format(day, 'd')}
                {event && (
                    <div
                        className="absolute -bottom-1 left-0 right-0 h-1.5 mx-auto w-5 rounded-full"
                        style={{ backgroundColor: event.color || '#3B82F6' }}
                        title={event.title}
                        aria-label={`Event: ${event.title}`}
                    />
                )}
            </button>
        </div>
    );
};

export default React.memo(DayCell);